/**
 * Socket Store
 *
 * Manages Socket.io connection and real-time events.
 */

import { create } from 'zustand';
import { io } from 'socket.io-client';
import { useAuthStore } from './authStore';
import { useNotificationStore } from './notificationStore';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || window.location.origin;

const useSocketStore = create((set, get) => ({
  socket: null,
  isConnected: false,
  onlineUsers: [],

  // Connect to socket server
  connect: () => {
    const { socket } = get();
    if (socket?.connected) return;

    const authStore = useAuthStore.getState();
    const token = authStore.accessToken;

    const newSocket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('Socket connected');
      set({ isConnected: true });
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      set({ isConnected: false });
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      set({ isConnected: false });
    });

    // User status events
    newSocket.on('user:online', (data) => {
      set((state) => {
        if (!state.onlineUsers.find(u => u.userId === data.userId)) {
          return { onlineUsers: [...state.onlineUsers, data] };
        }
        return state;
      });
    });

    newSocket.on('user:offline', (data) => {
      set((state) => ({
        onlineUsers: state.onlineUsers.filter(u => u.userId !== data.userId),
      }));
    });

    // Notification events
    newSocket.on('notification:new', (notification) => {
      const notificationStore = useNotificationStore.getState();
      notificationStore.addNotification(notification);
    });

    // Content events
    newSocket.on('content:new', (content) => {
      // Handle new content - could trigger a query refetch
      console.log('New content:', content);
    });

    newSocket.on('content:updated', (content) => {
      console.log('Content updated:', content);
    });

    newSocket.on('content:deleted', (data) => {
      console.log('Content deleted:', data.id);
    });

    // Admin events
    newSocket.on('admin:stats', (stats) => {
      console.log('Admin stats update:', stats);
    });

    newSocket.on('admin:newUser', (user) => {
      console.log('New user registered:', user);
    });

    set({ socket: newSocket });
  },

  // Disconnect from socket server
  disconnect: () => {
    const { socket } = get();
    if (socket) {
      socket.disconnect();
      set({ socket: null, isConnected: false, onlineUsers: [] });
    }
  },

  // Join a room
  joinRoom: (room) => {
    const { socket } = get();
    if (socket?.connected) {
      socket.emit('join:room', room);
    }
  },

  // Leave a room
  leaveRoom: (room) => {
    const { socket } = get();
    if (socket?.connected) {
      socket.emit('leave:room', room);
    }
  },

  // Emit typing indicator
  startTyping: (room) => {
    const { socket } = get();
    if (socket?.connected) {
      socket.emit('typing:start', { room });
    }
  },

  stopTyping: (room) => {
    const { socket } = get();
    if (socket?.connected) {
      socket.emit('typing:stop', { room });
    }
  },

  // Check if a user is online
  isUserOnline: (userId) => {
    const { onlineUsers } = get();
    return onlineUsers.some(u => u.userId === userId);
  },
}));

export { useSocketStore };
